import { Skeleton, Text, VStack } from 'ui_2';
import { Giveaway } from './Giveaway';
import {
  useAllGiveawaysQuery,
  useCurrentGiveawaysQuery,
} from '@src/hooks/useGiveaways';
import { RaffleV2 } from 'jigsaw';
import { TRPCClientErrorLike } from '@trpc/client';

const GiveawayError = ({ errorMessage }: { errorMessage?: string }) => {
  return (
    <VStack className='w-full items-center'>
      <Text>Error loading giveaway data.</Text>
      {errorMessage && <Text>{errorMessage}</Text>}
    </VStack>
  );
};

const GiveawayLoading = ({ skeletonCount = 2 }: { skeletonCount?: number }) => {
  return (
    <div className='grid w-full grid-cols-2 justify-items-center gap-4 sm:grid-cols-3 md:grid-cols-3 md:gap-x-8 md:gap-y-6 lg:grid-cols-4 lg:gap-x-10 lg:gap-y-8'>
      {[...Array(skeletonCount).keys()].map((_, ix) => (
        <Skeleton className='h-[150px] w-full' delay={ix} key={ix} />
      ))}
    </div>
  );
};

const GiveawayEmpty = () => {
  return (
    <VStack className='w-full items-center'>
      <Text>No active giveaways.</Text>
    </VStack>
  );
};

type GiveawaysProps = {
  isLoading: boolean;
  isError: boolean;
  error: TRPCClientErrorLike<any> | null;
  giveaways?: RaffleV2[];
  skeletonCount?: number;
};

const Giveaways = ({
  isLoading,
  isError,
  error,
  giveaways,
  skeletonCount,
}: GiveawaysProps) => {
  if (isError) {
    return <GiveawayError errorMessage={error?.message} />;
  } else if (isLoading || !giveaways) {
    return <GiveawayLoading skeletonCount={skeletonCount} />;
  } else if (giveaways.length === 0) {
    return <GiveawayEmpty />;
  } else {
    return (
      <div className='grid w-full grid-cols-2 justify-items-center gap-4 sm:grid-cols-3 md:grid-cols-3 md:gap-x-8 md:gap-y-6 lg:grid-cols-4  lg:gap-x-10 lg:gap-y-8'>
        {giveaways.map((giveaway, ix) => (
          <Giveaway giveaway={giveaway} key={ix} />
        ))}
      </div>
    );
  }
};

export const CurrentGiveaways = () => {
  const { data, error, isError, isLoading } = useCurrentGiveawaysQuery();

  return (
    <Giveaways
      isError={isError}
      isLoading={isLoading}
      error={error}
      giveaways={data}
    />
  );
};

export const AllGiveaways = () => {
  const { data, error, isError, isLoading } = useAllGiveawaysQuery();

  return (
    <Giveaways
      isError={isError}
      isLoading={isLoading}
      error={error}
      giveaways={data}
      skeletonCount={6}
    />
  );
};
